import type { FantasyContest, FantasyStandingRow, FantasyTeam } from '@brush/api-client';
import { Spinner } from '@brush/ui';
import { useState } from 'react';
import {
  amendedNotice,
  describeGap,
  describeMovement,
  formatPoints,
  formatWhen,
  movementFor,
  pausedNotice,
} from '../../fantasy/roster';
import { usePlayer } from '../../player';
import { useFantasySnapshot, useFantasySnapshots, useFantasyStandings } from '../../queries';
import { TeamBreakdown } from './TeamBreakdown';

/**
 * §9.2's standings: rank, team, points, members scoring, and movement since the previous snapshot.
 *
 * <b>Movement costs one extra read and no new state</b>, which is what §9.2 means by free: prior
 * snapshots are retained, so the previous board is fetched and compared. The snapshot list carries
 * no rows — the server omits them deliberately — so it is cheap, and it is only there to learn
 * which snapshot came before this one. Sequence numbers never reach the screen (§9.3).
 *
 * <b>A paused contest says so, above everything.</b> §6.2's Hold stops a contest updating
 * entirely, and §9.2 is explicit that this is the one state where "no update" is not "no change".
 * The flag is read from the contest detail; <b>`GET /drift` is never called</b> — it is admin-only,
 * it names events a player has no business seeing, and the 403 would read as a broken app.
 */
export function ContestStandings({
  contest,
  myTeam,
}: {
  contest: FantasyContest;
  myTeam: FantasyTeam | null;
}) {
  const { theme } = usePlayer();
  const standingsQ = useFantasyStandings(contest.id);
  const snapshotsQ = useFantasySnapshots(contest.id);
  const [openTeamId, setOpenTeamId] = useState<string | null>(null);

  const standings = standingsQ.data;
  const rows = standings?.data ?? [];

  // The one before the current one, by sequence. Read off the list rather than assumed to be
  // `current - 1`: nothing in the contract promises the numbering is contiguous, and a gap would
  // silently compare against nothing.
  const sequences = (snapshotsQ.data ?? []).map((s) => s.sequenceNumber).sort((a, b) => a - b);
  const currentSeq = standings?.sequenceNumber ?? null;
  const previousSeq =
    currentSeq === null
      ? null
      : (sequences.filter((n) => n < currentSeq).pop() ?? null);

  const previousQ = useFantasySnapshot(contest.id, previousSeq);
  // Null means "there is no previous board", which `movementFor` reads as "nothing to say" —
  // distinct from a team that is new to a board that does have one.
  const previousRows = previousSeq === null ? null : (previousQ.data?.rows ?? null);

  const card = { background: theme.panel, border: `1px solid ${theme.border}` };
  const leaderPoints = rows[0]?.points ?? 0;
  const mine = myTeam ? rows.find((r) => r.teamId === myTeam.id) : undefined;

  if (standingsQ.isLoading) return <Spinner label="Loading standings…" />;

  return (
    <div className="space-y-4">
      {contest.standingsPaused ? (
        <section
          role="status"
          style={{ background: theme.panel, border: `1px solid ${theme.accent}` }}
          className="rounded-xl p-5"
        >
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
            Scoring is paused
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed" style={{ color: theme.textMuted }}>
            {pausedNotice(contest.standingsPausedAt)}
          </p>
        </section>
      ) : null}

      {/* The sentence §9.3 asks for, about the reader's own team and nowhere else. A column of
          gaps on every row would be arithmetic nobody reads; this is the one somebody wants. */}
      {mine ? (
        <section style={card} className="rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
            Your team
          </div>
          <div className="mt-0.5 text-lg font-bold">{mine.teamName}</div>
          <p className="mt-1 text-sm" style={{ color: theme.accent }}>
            {mine.rank === 1 ? 'Leading' : `${ordinal(mine.rank)}${mine.isTied ? ' (tied)' : ''}`}
            {' · '}
            {describeGap(mine, leaderPoints)}
          </p>
          <p className="mt-1 text-xs" style={{ color: theme.textMuted }}>
            {mine.membersScoring} of {contest.rosterSize} of your picks{' '}
            {mine.membersScoring === 1 ? 'has' : 'have'} scored so far.
          </p>
        </section>
      ) : null}

      <section style={card} className="overflow-hidden rounded-xl">
        <header className="px-5 py-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
            Standings
          </h2>
          <p className="mt-1 text-xs" style={{ color: theme.textMuted }}>
            {/* Served from a frozen snapshot rather than computed, so "as of" is not decoration: a
                board can be days old and stale standings look exactly like current ones. */}
            {standings?.computedAt ? `Updated ${formatWhen(standings.computedAt)}` : 'Not scored yet'}
            {standings?.isFinal ? ' · final' : ''}
          </p>

          {/* §6.2 and §7.2's amendment, on the board it is a fact about. Chalk rather than the
              signal orange above it: orange means "this is you" in this app and is never a control,
              and an amendment is neither — it is something that happened to the whole contest.
              Below the "as of" line and above the rows, because it qualifies the figures. */}
          {standings?.amendedAfterLock ? (
            <p className="mt-2 text-xs leading-relaxed" style={{ color: theme.textMuted }}>
              <span style={{ color: theme.text }}>Amended after entries closed.</span>{' '}
              {amendedNotice()}
            </p>
          ) : null}
          {rows.length > 0 ? (
            <p className="mt-2 text-xs" style={{ color: theme.textMuted }}>
              Choose a team to see how its points were worked out.
            </p>
          ) : null}
        </header>

        {rows.length === 0 ? (
          <p className="p-8 text-center text-sm" style={{ color: theme.textMuted }}>
            {/* Two causes, two sentences — only one is worth checking back on. */}
            {currentSeq === null
              ? 'Nothing has been scored yet. Standings appear once the room works out the first results.'
              : 'Nobody entered this contest.'}
          </p>
        ) : (
          <ul>
            {rows.map((row) => (
              <StandingRow
                key={row.teamId}
                row={row}
                contest={contest}
                movement={describeMovement(movementFor(row, previousRows))}
                isMine={myTeam?.id === row.teamId}
                isOpen={openTeamId === row.teamId}
                onToggle={() => setOpenTeamId(openTeamId === row.teamId ? null : row.teamId)}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StandingRow({
  row,
  contest,
  movement,
  isMine,
  isOpen,
  onToggle,
}: {
  row: FantasyStandingRow;
  contest: FantasyContest;
  movement: string | null;
  isMine: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { theme } = usePlayer();

  return (
    <li
      style={{
        borderBottom: `1px solid ${theme.border}`,
        // Orange means "this is you" on this side of the glass, and this is the row it is for.
        background: isMine ? `${theme.accent}12` : 'transparent',
      }}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={onToggle}
        // Hover is white rather than the accent: lighting a row in the signal as the pointer
        // crosses would say "this is you" about whoever it happened to be over.
        className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition hover:bg-white/5"
      >
        <div className="flex min-w-0 items-center gap-3">
          <span
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
            style={{
              background: row.rank === 1 ? theme.accent : theme.border,
              color: row.rank === 1 ? theme.bg : theme.text,
            }}
          >
            {/* The snapshot's rank, never the row number: tied teams share one and the next skips
                (1, 2, 2, 4). */}
            {row.rank}
          </span>
          <div className="min-w-0">
            <div className="truncate font-semibold">
              {row.teamName}
              {isMine ? (
                <span
                  className="ml-2 text-xs uppercase tracking-wider"
                  style={{ color: theme.accent }}
                >
                  You
                </span>
              ) : null}
            </div>
            <div className="text-xs" style={{ color: theme.textMuted }}>
              {row.membersScoring} of {contest.rosterSize} scoring
              {row.isTied ? ' · tied' : ''}
              {movement ? ` · ${movement}` : ''}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <div className="font-bold" style={{ color: theme.accent }}>
              {formatPoints(row.points)}
            </div>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
              pts
            </div>
          </div>
          <span
            aria-hidden
            className="text-xs transition-transform"
            style={{ color: theme.textMuted, transform: isOpen ? 'rotate(90deg)' : 'none' }}
          >
            ▶
          </span>
        </div>
      </button>

      {isOpen ? <TeamBreakdown contest={contest} teamId={row.teamId} inline /> : null}
    </li>
  );
}

/** "1st", "22nd" — a rank as somebody says it out loud. */
function ordinal(place: number): string {
  const mod100 = place % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${place}th`;
  return `${place}${['th', 'st', 'nd', 'rd'][place % 10] ?? 'th'}`;
}
